import { ProtocolError } from './errors.js';

export type FileMeta = Readonly<{
  name: string;
  size: number;
  mimeType: string;
}>;

/** Control messages sent by the sender over the data channel (as JSON text). */
export type SenderControl =
  | Readonly<{ type: 'meta'; file: FileMeta }>
  | Readonly<{ type: 'done' }>;

/** Control messages sent by the receiver over the data channel (as JSON text). */
export type ReceiverControl =
  | Readonly<{ type: 'accept' }>
  | Readonly<{ type: 'reject'; reason: string }>
  | Readonly<{ type: 'progress'; receivedBytes: number }>
  | Readonly<{ type: 'complete' }>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJson = (raw: string): Record<string, unknown> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new ProtocolError(`Control message is not valid JSON: ${String(error)}`);
  }
  if (!isRecord(parsed)) {
    throw new ProtocolError('Control message must be a JSON object');
  }
  return parsed;
};

export const parseFileMeta = (value: unknown): FileMeta => {
  if (!isRecord(value)) {
    throw new ProtocolError('File metadata must be an object');
  }
  const { name, size, mimeType } = value;
  if (typeof name !== 'string' || name.length === 0) {
    throw new ProtocolError('File name must be a non-empty string');
  }
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
    throw new ProtocolError(`File size must be a non-negative integer, got ${String(size)}`);
  }
  if (typeof mimeType !== 'string') {
    throw new ProtocolError('File mimeType must be a string');
  }
  return { name, size, mimeType };
};

export const parseSenderControl = (raw: string): SenderControl => {
  const message = parseJson(raw);
  switch (message['type']) {
    case 'meta':
      return { type: 'meta', file: parseFileMeta(message['file']) };
    case 'done':
      return { type: 'done' };
    default:
      throw new ProtocolError(`Unknown sender control type: ${String(message['type'])}`);
  }
};

export const parseReceiverControl = (raw: string): ReceiverControl => {
  const message = parseJson(raw);
  switch (message['type']) {
    case 'accept':
      return { type: 'accept' };
    case 'reject': {
      const reason = message['reason'];
      return { type: 'reject', reason: typeof reason === 'string' ? reason : 'Receiver rejected the file' };
    }
    case 'progress': {
      const receivedBytes = message['receivedBytes'];
      if (typeof receivedBytes !== 'number' || !Number.isInteger(receivedBytes) || receivedBytes < 0) {
        throw new ProtocolError(`progress.receivedBytes must be a non-negative integer, got ${String(receivedBytes)}`);
      }
      return { type: 'progress', receivedBytes };
    }
    case 'complete':
      return { type: 'complete' };
    default:
      throw new ProtocolError(`Unknown receiver control type: ${String(message['type'])}`);
  }
};

/** Number of chunks needed to transfer `sizeBytes` in pieces of `chunkSize`. */
export const countChunks = (sizeBytes: number, chunkSize: number): number => {
  if (chunkSize <= 0) {
    throw new RangeError(`chunkSize must be positive, got ${chunkSize}`);
  }
  return Math.ceil(sizeBytes / chunkSize);
};

export type ByteRange = Readonly<{ start: number; end: number }>;

/** Byte range [start, end) of chunk number `index`. */
export const chunkRange = (index: number, sizeBytes: number, chunkSize: number): ByteRange => {
  const start = index * chunkSize;
  if (start >= sizeBytes && sizeBytes > 0) {
    throw new RangeError(`Chunk ${index} is out of range for ${sizeBytes} bytes`);
  }
  return { start, end: Math.min(start + chunkSize, sizeBytes) };
};

/** True when enough new bytes arrived since the last report to justify another progress message. */
export const shouldReportProgress = (
  lastReportedBytes: number,
  receivedBytes: number,
  intervalBytes: number,
): boolean => receivedBytes - lastReportedBytes >= intervalBytes;
